import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewRef } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../shared/services/auth.service';
import { UserSessionService } from '../shared/services/user-session.service';
import { UserRoles } from '../shared/model/userrole';
import { SignalrService } from '../shared/services/signalR.service';
import { EventNotficationMessage } from '../shared/model/eventNotficationMessage';
import { AudioService } from '../shared/services/audioService';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { ServiceWorkerRegisteration } from '../shared/services/service.worker.registeration';
import { mapEventTypeToImage } from '../shared/Utilities/eventTypeImageConverter';
import { EventType } from '../shared/model/eventtype';
import { DepartmentService } from '../shared/services/department.service';
import Roles from '../shared/model/user/Roles';
import { getFormattedDate } from '../shared/Utilities/timeFormatter';
import { Subscription } from 'rxjs';
import { navItems } from './side-nav/navigation-items';
import { CarehomeAssignment } from '../shared/model/user/UserBasicInfo';
import { ToastService } from '../shared/services/toast.service';

@Component({
	selector: 'app-dashboard',
	templateUrl: './common-layout.component.html',
	styleUrls: ['./common-layout.component.scss']
})

export class CommonLayoutComponent implements OnInit, OnDestroy {
	@ViewChild('logoutForm', { static: true }) logoutForm: ElementRef<HTMLFormElement>;

	public app: any;
	public headerThemes: any;
	public changeHeader: any;
	public sidenavThemes: any;
	public changeSidenav: any;
	public headerSelected: any;
	public sidenavSelected: any;
	public searchActived: any;
	public searchModel: any;
	public isDisabledNotification: boolean;
	frontendHomeUrl = window.location.origin + '/';
	notificationSubscription: Subscription;
	private signalRInitialized = false;
	subscriptionData = {
		endpoint: null,
		key: null,
		secret: null
	};

	isAdmin = false;
	routerLink = '';
	today = new Date();
	offlineDate: Date | null = null;

	navItems = navItems;
	careHomeAssignments: any[] = [];
	selectedCareHome: CarehomeAssignment = null;

	constructor(public router: Router,
		private translate: TranslateService,
		public authService: AuthService,
		public userSessionService: UserSessionService,
		private signalRService: SignalrService,
		private departmentService: DepartmentService,
		private changeDetectorRef: ChangeDetectorRef,
		private toastService: ToastService,
		private audioService: AudioService,
		private serviceWorkerRegisteration: ServiceWorkerRegisteration) {
		this.app = {
			layout: {
				sidePanelOpen: false,
				isMenuOpened: true,
				isMenuCollapsed: false,
				themeConfigOpen: false,
				rtlActived: false,
				searchActived: false
			}
		};

		this.headerThemes = ['header-default', 'header-primary', 'header-info', 'header-success', 'header-danger', 'header-dark'];
		this.changeHeader = changeHeader;

		function changeHeader(headerTheme) {
			this.headerSelected = headerTheme;
		}

		this.sidenavThemes = ['sidenav-default', 'side-nav-dark'];
		this.changeSidenav = changeSidenav;

		function changeSidenav(sidenavTheme) {
			this.sidenavSelected = sidenavTheme;
		}

		if (this.userSessionService.userHasRole(Roles.Admin)) {
			this.routerLink = '/carehomes';
		} else {
			this.routerLink = '/statusoverview/statusoverview';
		}
	}

	ngOnInit() {
		this.askForPlayAudioPermission();
		this.addSignalR();
		this.addWebPushRegisteration();
		this.validateUserRole();
		this.addNetworkEventListeners();
		this.loadCareHomeAssignments();
	}

	ngOnDestroy() {
		this.notificationSubscription?.unsubscribe();
		this.signalRService.disconnect().catch(console.error);
		this.signalRInitialized = false;
	}

	notifyUser(message: EventNotficationMessage) {
		const body = `${message.residentName} at ${(new Date(message.occurrence)).toLocaleString()}`;
		const icon = `assets/images/icons/${mapEventTypeToImage(message.eventType)}`;
		this.toastService.custom('info', body, message.notificationText, 15000, icon);
	}

	logOut() {
		this.signalRService.disconnect().then(() => {
			this.userSessionService.clearUserSession();
			this.logoutForm?.nativeElement.submit();
		}).catch(console.error);

		return false;
	}

	validateUserRole() {
		this.isAdmin = false;
	}

	addSignalR() {
		if(this.isGlobalAdmin()) {
			return;
		}
		if (this.signalRInitialized) {
			return;
		}

		this.signalRInitialized = true;
		this.signalRService.startConnection();
		this.signalRService.subscribeToEventsUpdate();

		this.notificationSubscription = this.signalRService.eventNotficationMessage.subscribe((message: EventNotficationMessage) => {
			const selectedDepartmentIds = this.departmentService.getCurrentDepartmentIds();
			const allDepartmentsSelected = selectedDepartmentIds.length === 0;

			if (message
				&& (allDepartmentsSelected || (message.departmentIds || []).some(id => selectedDepartmentIds.includes(id)))
				&& !this.isDisabledNotification) {
				this.notifyUser(message);
				// The toast always shows; only the sound is policy-driven. A silenced event is quiet, not
				// hidden. Compared against false explicitly so a message from an older backend, which carries
				// no verdict at all, still sounds.
				if (message.isSounded !== false) {
					this.audioService.playNotificationSound();
				}
			}
		});
	}

	private async reconnectSignalR(): Promise<void> {
		this.notificationSubscription?.unsubscribe();
		await this.signalRService.disconnect();
		this.signalRInitialized = false;
		this.addSignalR();
	}

	askForPlayAudioPermission() {
		this.audioService.approvePlayAudio(true);
		return;
		if (this.audioService.playAudioApproved) {
			return;
		}

		Swal.fire({
			text: "Allow events notification sound",
			icon: 'info',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes',
		}).then((result) => {
			if (result.value) {
				this.audioService.approvePlayAudio(result.value);
			} else {
				this.audioService.approvePlayAudio(false);
			}
		});
	}

	addWebPushRegisteration() {
		if (this.userSessionService.userHasRole(Roles.Admin)) {
			return;
		}
		if (this.userSessionService.getSelectedCarehome() == null) {
			return;
		}
		this.serviceWorkerRegisteration
			.getNotificationSubscription()
			.subscribe(data => {
				this.isDisabledNotification = data.body && data.body.Disabled;
			}, error => console.error(error));

		// why we make this call when subscriptionData is always empty?
		// this.serviceWorkerRegisteration.registerPushNotificationSubscription(this.subscriptionData);
		this.serviceWorkerRegisteration.initializeServiceWorker();
	}

	detectChanges() {
		if (this.changeDetectorRef && !(this.changeDetectorRef as ViewRef).destroyed) {
			this.changeDetectorRef.detectChanges();
		}
	}

	addNetworkEventListeners() {
		window.addEventListener("online", () => {
			this.offlineDate = null;
		});

		window.addEventListener("offline", () => {
			this.offlineDate = new Date();
		});
	}

	getFormattedOfflineDate(): string {
		return getFormattedDate(this.offlineDate);
	}

	getFormattedOfflineTime(): string {
		return this.offlineDate.toLocaleTimeString();
	}

	disableNotificationToggle() {
		this.isDisabledNotification = !this.isDisabledNotification;
		this.serviceWorkerRegisteration.toggleNotificationSubscribtion(this.isDisabledNotification);
	}

	isGlobalAdmin(): boolean {
		return this.userSessionService.userHasRole(Roles.Admin);
	}

	loadCareHomeAssignments(): void {
		this.careHomeAssignments = this.userSessionService.getCarehomeAssignments();
		const selectedId = this.userSessionService.getSelectedCarehome();

		if (this.careHomeAssignments.length > 0) {
			if (selectedId) {
				this.selectedCareHome = this.careHomeAssignments.find(a => a.CarehomeId === selectedId);
			}
		}
	}

	selectCareHome(assignment: CarehomeAssignment): void {
		// Return early if selecting the same care home
		if (this.selectedCareHome && this.selectedCareHome.CarehomeId === assignment.CarehomeId) {
			return;
		}

		// Show loading overlay
		Swal.fire({
			title: this.translate.instant('common.switching'),
			text: this.translate.instant('common.pleaseWait'),
			allowOutsideClick: false,
			allowEscapeKey: false,
			showConfirmButton: false,
			didOpen: () => {
				Swal.showLoading();
			}
		});

		this.authService.switchCarehome(assignment.CarehomeId).subscribe({
			next: async (response) => {
				const userData = response.body;
				if (userData) {
					this.userSessionService.initializeUserSession(userData);
				}

				await this.reconnectSignalR();
				this.addWebPushRegisteration();

				// Reload the app without full browser reload
				const targetRoute = this.userSessionService.getDefaultLandingPageByRole();
				await this.router.navigateByUrl('/', { skipLocationChange: true });
				await this.router.navigate([targetRoute]);
				Swal.close();
			},
			error: (err) => {
				Swal.close();
				this.toastService.error(
					this.translate.instant('common.switchCarehomeFailed'),
					this.translate.instant('common.error'),
					5000
				);
				console.error('Failed to switch care home', err);
			}
		});
	}

	getSelectedCareHomeName(): string {
		const message = this.translate.instant('common.noCarehome');
		return this.selectedCareHome ? this.selectedCareHome.CarehomeName : message;
	}
}